import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Rocket } from "lucide-react";
import api from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { toast } from "sonner";

// Map API paywall_action codes → localised marketing copy
const ACTION_COPY = {
  project_limit: { titleKey: "paywall.projectLimit.title", descKey: "paywall.projectLimit.desc" },
  task_limit: { titleKey: "paywall.taskLimit.title", descKey: "paywall.taskLimit.desc" },
  unarchive_project: { titleKey: "paywall.unarchive.title", descKey: "paywall.unarchive.desc" },
  export: { titleKey: "paywall.export.title", descKey: "paywall.export.desc" },
  add_teammate: { titleKey: "paywall.addTeammate.title", descKey: "paywall.addTeammate.desc" },
  ai_generator: { titleKey: "paywall.ai.title", descKey: "paywall.ai.desc" },
  voice_command: { titleKey: "paywall.voice.title", descKey: "paywall.voice.desc" },
  custom_logo: { titleKey: "paywall.customLogo.title", descKey: "paywall.customLogo.desc" },
  default: { titleKey: "paywall.default.title", descKey: "paywall.default.desc" },
};

export default function PaywallModal({ open, onOpenChange, action = "default", message }) {
  const { t } = useTranslation();
  const copy = ACTION_COPY[action] || ACTION_COPY.default;
  const [loading, setLoading] = React.useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "flowgantt_pro_monthly",
        origin_url: window.location.origin,
        // Attribution: which paywall triggered the conversion
        metadata: { paywall_action: action },
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err, t("common.error")));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="paywall-modal"
        data-paywall-action={action}
        className="rounded-sm max-w-md"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-1">
            <Sparkles className="w-3.5 h-3.5" />
            {t("paywall.eyebrow")}
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {t(copy.titleKey)}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-600">
            {message || t(copy.descKey)}
          </DialogDescription>
        </DialogHeader>

        <div className="border border-stone-200 rounded-sm p-4 bg-stone-50 space-y-2">
          <div className="flex items-baseline gap-2">
            <span className="font-display font-black text-3xl tracking-tighter">9€</span>
            <span className="text-xs text-neutral-500">{t("paywall.perMonth")}</span>
          </div>
          <ul className="text-xs text-neutral-700 space-y-1">
            <li>• {t("paywall.bullet.unlimited")}</li>
            <li>• {t("paywall.bullet.export")}</li>
            <li>• {t("paywall.bullet.team")}</li>
            <li>• {t("paywall.bullet.ai")}</li>
          </ul>
        </div>

        <DialogFooter className="pt-2 gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="paywall-later-btn"
            className="rounded-sm text-xs uppercase tracking-[0.15em]"
          >
            {t("paywall.later")}
          </Button>
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            data-testid="paywall-upgrade-btn"
            className="rounded-sm bg-[#E05236] hover:bg-[#C94A31] text-white h-11 px-5"
          >
            <Rocket className="w-3.5 h-3.5 mr-2" />
            {loading ? "..." : t("paywall.upgrade")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
